import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { MaintenanceEntry, Part, Truck, User } from '../types';
import { fleetOpsService, FleetOpsWorkspace } from './FleetOpsService';

export interface WorkspaceSnapshot {
  scopeId: string;
  ownerEmail?: string;
  users?: Array<{ id: string; email: string; name: string; role: string }>;
  appState: {
    trucks: Truck[];
    maintenance: MaintenanceEntry[];
    parts: Part[];
  };
  fleetOpsWorkspace: FleetOpsWorkspace;
  legacyLocalStorage?: Record<string, string>;
  updatedAt: string;
}

export interface PlatformUsageRecord {
  scopeId: string;
  ownerEmail?: string;
  users: Array<{ id: string; email: string; name: string; role: string }>;
  trucks: number;
  maintenance: number;
  parts: number;
  inspections: number;
  issues: number;
  workOrders: number;
  compliance: number;
  updatedAt: string;
}

const REGISTERED_USERS_KEY = 'fleetfix_registered_users';
const SNAPSHOT_PREFIX = 'fleetfix_workspace_snapshot_';
const PUBLIC_APP_URL = process.env.REACT_APP_PUBLIC_APP_URL || 'https://fleet-fix-alpha.vercel.app';
const WORKSPACE_RESTORED_EVENT = 'fleetfix:workspace-restored';
const LEGACY_GLOBAL_STORAGE_KEYS = [
  'fleetfix_app_state',
  'downtimeRecords',
  'calendarEvents',
  'maintenanceTemplates',
  'maintenanceSchedules',
  'fleetSettings',
  'maintenanceSettings',
  'userSettings',
  'dataRetentionSettings',
  'notificationSettings',
  'fleetfix-theme'
];

const normalize = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const getWorkspaceSyncApiUrl = (): string => {
  const configuredUrl = process.env.REACT_APP_WORKSPACE_SYNC_API_URL?.trim();
  if (configuredUrl) return configuredUrl;

  if (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return `${PUBLIC_APP_URL.replace(/\/+$/, '')}/api/workspace-sync`;
  }

  return '/api/workspace-sync';
};

const getUserScope = (user: User): string => (
  user.companyId && user.companyId !== 'default-company' ? user.companyId : user.id
);

interface RegisteredUserRecord {
  id: string;
  email: string;
  name: string;
  role: string;
  companyId: string;
  lastSeenAt: string;
}

class TeamWorkspaceService {
  private lastPublishedSignatureByScope = new Map<string, string>();

  getRegisteredUsers(): RegisteredUserRecord[] {
    try {
      return JSON.parse(localStorage.getItem(REGISTERED_USERS_KEY) || '[]');
    } catch {
      return [];
    }
  }

  async recordUser(user: User): Promise<void> {
    if (!user) return;
    const normalizedEmail = user.email.trim().toLowerCase();
    const current = this.getRegisteredUsers().filter(item => item.email.toLowerCase() !== normalizedEmail);
    const record = {
      id: user.id,
      email: normalizedEmail,
      name: user.name,
      role: user.role,
      companyId: getUserScope(user),
      lastSeenAt: new Date().toISOString()
    };
    localStorage.setItem(REGISTERED_USERS_KEY, JSON.stringify([record, ...current].slice(0, 500)));

    try {
      await setDoc(doc(db, 'registeredUsers', user.id), {
        ...record,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.warn('Unable to sync registered user record:', error);
    }
  }

  async publishSnapshot(user: User, appState: { trucks: Truck[]; maintenance: MaintenanceEntry[]; parts: Part[] }): Promise<void> {
    const scopeId = getUserScope(user);
    const existing = this.readCachedSnapshot(scopeId);
    const userRecord = { id: user.id, email: user.email.toLowerCase(), name: user.name, role: user.role };
    const users = [
      userRecord,
      ...(existing?.users || []).filter(item => item.email.toLowerCase() !== userRecord.email)
    ];
    const snapshot: WorkspaceSnapshot = {
      scopeId,
      ownerEmail: existing?.ownerEmail || user.email.toLowerCase(),
      users,
      appState: normalize(appState),
      fleetOpsWorkspace: normalize(fleetOpsService.getWorkspace(scopeId)),
      legacyLocalStorage: this.captureLegacyLocalStorage(scopeId),
      updatedAt: new Date().toISOString()
    };
    const signature = this.getSnapshotSignature(snapshot);
    if (this.lastPublishedSignatureByScope.get(scopeId) === signature) return;

    localStorage.setItem(`${SNAPSHOT_PREFIX}${scopeId}`, JSON.stringify(snapshot));

    let published = false;
    try {
      await this.saveSnapshotRemotely(scopeId, snapshot);
      published = true;
    } catch (error) {
      console.warn('Server workspace sync failed, trying direct Firestore snapshot sync:', error);
      try {
        await setDoc(doc(db, 'workspaceSnapshots', scopeId), {
          ...snapshot,
          updatedAt: serverTimestamp()
        }, { merge: true });
        published = true;
      } catch (firestoreError) {
        console.warn('Unable to sync workspace snapshot:', firestoreError);
      }
    }

    if (published) {
      this.lastPublishedSignatureByScope.set(scopeId, signature);
    }
  }

  async loadSnapshot(scopeId: string, includeRemote = true): Promise<WorkspaceSnapshot | null> {
    if (includeRemote) {
      const remote = await this.loadSnapshotRemotely(scopeId);
      if (remote) {
        return remote;
      }

      try {
        const snapshotDoc = await getDoc(doc(db, 'workspaceSnapshots', scopeId));
        if (snapshotDoc.exists()) {
          return snapshotDoc.data() as WorkspaceSnapshot;
        }
      } catch (error) {
        console.warn('Unable to load remote workspace snapshot:', error);
      }
    }

    const local = localStorage.getItem(`${SNAPSHOT_PREFIX}${scopeId}`);
    if (local) {
      const snapshot = this.readCachedSnapshot(scopeId);
      if (snapshot) {
        return snapshot;
      }
    }

    return null;
  }

  async listUsageRecords(): Promise<PlatformUsageRecord[]> {
    const records = new Map<string, PlatformUsageRecord>();
    const addSnapshot = (snapshot: WorkspaceSnapshot) => {
      records.set(snapshot.scopeId, {
        scopeId: snapshot.scopeId,
        ownerEmail: snapshot.ownerEmail,
        users: snapshot.users || [],
        trucks: snapshot.appState?.trucks?.length || 0,
        maintenance: snapshot.appState?.maintenance?.length || 0,
        parts: snapshot.appState?.parts?.length || 0,
        inspections: snapshot.fleetOpsWorkspace?.inspections?.length || 0,
        issues: snapshot.fleetOpsWorkspace?.issueReports?.length || 0,
        workOrders: snapshot.fleetOpsWorkspace?.workOrders?.length || 0,
        compliance: snapshot.fleetOpsWorkspace?.complianceDocuments?.length || 0,
        updatedAt: snapshot.updatedAt || new Date().toISOString()
      });
    };
    const addRegisteredUser = (user: Partial<RegisteredUserRecord>) => {
      if (!user.email) return;
      const scopeId = user.companyId || user.id;
      if (!scopeId) return;

      const normalizedEmail = user.email.toLowerCase();
      const userSummary = {
        id: user.id || normalizedEmail,
        email: normalizedEmail,
        name: user.name || normalizedEmail.split('@')[0],
        role: user.role || 'viewer'
      };
      const current = records.get(scopeId);
      if (current) {
        const nextUsers = [
          userSummary,
          ...current.users.filter(existing => existing.email.toLowerCase() !== normalizedEmail)
        ];
        records.set(scopeId, {
          ...current,
          ownerEmail: current.ownerEmail || normalizedEmail,
          users: nextUsers,
          updatedAt: new Date(user.lastSeenAt || current.updatedAt).getTime() > new Date(current.updatedAt).getTime()
            ? user.lastSeenAt || current.updatedAt
            : current.updatedAt
        });
        return;
      }

      records.set(scopeId, {
        scopeId,
        ownerEmail: normalizedEmail,
        users: [userSummary],
        trucks: 0,
        maintenance: 0,
        parts: 0,
        inspections: 0,
        issues: 0,
        workOrders: 0,
        compliance: 0,
        updatedAt: user.lastSeenAt || new Date().toISOString()
      });
    };

    Object.keys(localStorage)
      .filter(key => key.startsWith(SNAPSHOT_PREFIX))
      .forEach(key => {
        try {
          addSnapshot(JSON.parse(localStorage.getItem(key) || '{}') as WorkspaceSnapshot);
        } catch {
          localStorage.removeItem(key);
        }
      });

    try {
      const snapshotDocs = await getDocs(collection(db, 'workspaceSnapshots'));
      snapshotDocs.forEach(snapshotDoc => addSnapshot(snapshotDoc.data() as WorkspaceSnapshot));
    } catch (error) {
      console.warn('Unable to load remote usage records:', error);
    }

    this.getRegisteredUsers().forEach(addRegisteredUser);

    try {
      const registeredUserDocs = await getDocs(collection(db, 'registeredUsers'));
      registeredUserDocs.forEach(userDoc => addRegisteredUser({
        ...(userDoc.data() as RegisteredUserRecord),
        id: userDoc.id
      }));
    } catch (error) {
      console.warn('Unable to load remote registered users:', error);
    }

    return Array.from(records.values()).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await auth.currentUser?.getIdToken().catch(() => null);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async requestWorkspaceSync(payload: Record<string, unknown>): Promise<any> {
    const authHeaders = await this.getAuthHeaders();
    if (!('Authorization' in authHeaders)) {
      throw new Error('A signed-in Firebase session is required for workspace sync.');
    }

    const response = await fetch(getWorkspaceSyncApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.error || 'Workspace sync failed.');
    }
    return result;
  }

  private async loadSnapshotRemotely(scopeId: string): Promise<WorkspaceSnapshot | null> {
    try {
      const result = await this.requestWorkspaceSync({ action: 'load', scopeId });
      return result.snapshot || null;
    } catch (error) {
      console.warn('Workspace sync API load failed:', error);
      return null;
    }
  }

  private async saveSnapshotRemotely(scopeId: string, snapshot: WorkspaceSnapshot): Promise<void> {
    await this.requestWorkspaceSync({ action: 'save', scopeId, snapshot });
  }

  private readCachedSnapshot(scopeId: string): WorkspaceSnapshot | null {
    if (typeof window === 'undefined') return null;

    const local = localStorage.getItem(`${SNAPSHOT_PREFIX}${scopeId}`);
    if (!local) return null;

    try {
      return JSON.parse(local) as WorkspaceSnapshot;
    } catch {
      localStorage.removeItem(`${SNAPSHOT_PREFIX}${scopeId}`);
      return null;
    }
  }

  private getLegacyScopedStorageKeys(scopeId: string): string[] {
    return [
      `fleetfix_downtime_${scopeId}`,
      `user_data_${scopeId}`,
      `notifications_${scopeId}`,
      `notificationPreferences_${scopeId}`
    ];
  }

  private captureLegacyLocalStorage(scopeId: string): Record<string, string> {
    if (typeof window === 'undefined') return {};

    return [...LEGACY_GLOBAL_STORAGE_KEYS, ...this.getLegacyScopedStorageKeys(scopeId)].reduce((bundle, key) => {
      const value = localStorage.getItem(key);
      if (value !== null) bundle[key] = value;
      return bundle;
    }, {} as Record<string, string>);
  }

  private getSnapshotSignature(snapshot: WorkspaceSnapshot): string {
    return JSON.stringify({
      appState: snapshot.appState,
      fleetOpsWorkspace: snapshot.fleetOpsWorkspace,
      legacyLocalStorage: snapshot.legacyLocalStorage || {}
    });
  }

  private getLegacyRecordCount(legacyLocalStorage: Record<string, string>): number {
    return Object.entries(legacyLocalStorage).reduce((count, [key, value]) => {
      if (!/(downtimeRecords|calendarEvents|maintenanceTemplates|maintenanceSchedules|user_data_)/.test(key)) {
        return count;
      }

      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return count + parsed.length;
        if (parsed && typeof parsed === 'object') {
          return count + Object.values(parsed as Record<string, unknown>).reduce((innerCount: number, entry: unknown) => (
            Array.isArray(entry) ? innerCount + entry.length : innerCount
          ), 0);
        }
      } catch {
        return count;
      }

      return count;
    }, 0);
  }

  getLocalWorkspaceRecordCount(scopeId: string, appState: { trucks: Truck[]; maintenance: MaintenanceEntry[]; parts: Part[] }): number {
    const fleetOpsWorkspace = fleetOpsService.getWorkspace(scopeId);
    return (
      (appState.trucks?.length || 0) +
      (appState.maintenance?.length || 0) +
      (appState.parts?.length || 0) +
      (fleetOpsWorkspace.inspections?.length || 0) +
      (fleetOpsWorkspace.issueReports?.length || 0) +
      (fleetOpsWorkspace.workOrders?.length || 0) +
      (fleetOpsWorkspace.complianceDocuments?.length || 0) +
      (fleetOpsWorkspace.telematicsEvents?.length || 0) +
      (fleetOpsWorkspace.teamMembers?.length || 0) +
      this.getLegacyRecordCount(this.captureLegacyLocalStorage(scopeId))
    );
  }

  restoreSnapshotLocally(scopeId: string, snapshot: WorkspaceSnapshot): void {
    if (typeof window === 'undefined') return;

    localStorage.setItem(`${SNAPSHOT_PREFIX}${scopeId}`, JSON.stringify(snapshot));
    localStorage.setItem(`fleetfix_trucks_${scopeId}`, JSON.stringify(snapshot.appState?.trucks || []));
    localStorage.setItem(`fleetfix_maintenance_${scopeId}`, JSON.stringify(snapshot.appState?.maintenance || []));
    localStorage.setItem(`fleetfix_parts_${scopeId}`, JSON.stringify(snapshot.appState?.parts || []));

    Object.entries(snapshot.legacyLocalStorage || {}).forEach(([key, value]) => {
      if (typeof value === 'string') {
        localStorage.setItem(key, value);
      }
    });

    if (snapshot.fleetOpsWorkspace) {
      fleetOpsService.saveWorkspace(scopeId, snapshot.fleetOpsWorkspace);
    }

    window.dispatchEvent(new CustomEvent(WORKSPACE_RESTORED_EVENT, { detail: { scopeId } }));
  }
}

export const teamWorkspaceService = new TeamWorkspaceService();
export default teamWorkspaceService;
